import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './UploadModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCloudArrowUp,
  faSearch,
  faCheck,
  faChevronDown,
  faChevronUp,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import BulkUpload from './BulkUpload';
import { useUser } from '../../UserContext';
import {
  DEFAULT_R2_CONTENT_TYPE,
  confirmR2Upload,
  formatBytes,
  formatBytesPerSecond,
  formatDuration,
  putFileToR2,
  requestR2UploadUrl
} from './r2UploadTransport';

const createEmptyProgress = () => ({ read1: 0, read2: 0, overall: 0 });
const createEmptyFileStats = () => ({
  loadedBytes: 0,
  totalBytes: 0,
  speedBytesPerSecond: 0,
  etaSeconds: null
});
const createEmptyStats = () => ({
  read1: createEmptyFileStats(),
  read2: createEmptyFileStats(),
  overall: createEmptyFileStats()
});
const createEmptyMetricsRef = () => ({
  loadedBytes: { read1: 0, read2: 0 },
  totalBytes: { read1: 0, read2: 0 },
  speedBytesPerSecond: { read1: 0, read2: 0 }
});

const normalizeKitInfo = (kitInfo) => {
  if (!kitInfo) return null;
  return {
    activationId: kitInfo.activationId,
    patientName: kitInfo.patientName,
    patientEmail: kitInfo.patientEmail
  };
};

const resolveUploadUrl = (descriptor) => descriptor.uploadUrl || descriptor.upload_url;

const resolveR2Headers = (descriptor) => {
  const headers = { ...(descriptor.headers || {}) };
  if (!Object.keys(headers).some(key => key.toLowerCase() === 'content-type')) {
    headers['Content-Type'] = DEFAULT_R2_CONTENT_TYPE;
  }
  return headers;
};

const sortPatientsByNumber = (patientList) => [...patientList].sort((a, b) => {
  const aNum = parseInt(a.patient_id?.split('_')[2], 10) || 0;
  const bNum = parseInt(b.patient_id?.split('_')[2], 10) || 0;
  return aNum - bNum;
});

const normalizeSearchPatient = (patient) => {
  const patientData = patient.patientdata || {};
  return {
    ...patientData,
    patient_id: patient.patient_id || patientData.patient_id,
    name: patient.name ?? patientData.name,
    parentProjectId: patient.parentProjectId ?? patientData.parentProjectId,
    parentProjectName: patient.parentProjectName ?? patientData.parentProjectName,
    subprojectId: patient.subprojectId ?? patientData.subprojectId,
    subprojectDescription: patient.subprojectDescription ?? patientData.subprojectDescription
  };
};

const getPatientBarcode = (patient) => {
  const patientData = patient.patientdata || patient;
  const barcodeEntry = Object.entries(patientData).find(([key, value]) =>
    key.toLowerCase().includes('barcode') && value !== null && value !== undefined && value !== ''
  );
  return barcodeEntry ? String(barcodeEntry[1]) : '';
};

function UploadModal({ isOpen, onClose, onUploadStatusChange, externalPatientId, kitInfo }) {
  const { user } = useUser();
  const [parentProjects, setParentProjects] = useState([]);
  const [selectedParentId, setSelectedParentId] = useState(null);
  const [expandedParentId, setExpandedParentId] = useState(null);
  const [subprojects, setSubprojects] = useState({});
  const [selectedSubprojectId, setSelectedSubprojectId] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState('');
  const [selectedSample, setSelectedSample] = useState('');
  const [isAnalysisLocked, setIsAnalysisLocked] = useState(false);
  const [isSampleLocked, setIsSampleLocked] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState({ read1: null, read2: null });
  const [patients, setPatients] = useState([]);
  const [targetSearch, setTargetSearch] = useState('');
  const [targetSearchResults, setTargetSearchResults] = useState([]);
  const [targetSearchLoading, setTargetSearchLoading] = useState(false);
  const [targetSearchError, setTargetSearchError] = useState('');
  const [isTargetSearchFocused, setIsTargetSearchFocused] = useState(false);
  const [analysisTypes, setAnalysisTypes] = useState({});
  const [sampleTypes, setSampleTypes] = useState({});
  const [supportedSamples, setSupportedSamples] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(createEmptyProgress());
  const [uploadStats, setUploadStats] = useState(createEmptyStats());
  const [isUploading, setIsUploading] = useState(false);
  const [currentFileName, setCurrentFileName] = useState('');
  const [uploadStage, setUploadStage] = useState('');
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  
  const uploadControllerRef = useRef(null);
  const uploadMetricsRef = useRef(createEmptyMetricsRef());
  const pendingSearchSelectionRef = useRef(null);
  const selectedProjectRef = useRef(null);
  const selectedSubprojectRef = useRef(null);
  const selectedPatientRef = useRef(null);

  const resetUploadTelemetry = () => {
    uploadMetricsRef.current = createEmptyMetricsRef();
    setUploadProgress(createEmptyProgress());
    setUploadStats(createEmptyStats());
    setCurrentFileName('');
    setUploadStage('');
  };

  const resetSelections = () => {
    setSelectedParentId(null);
    setSelectedSubprojectId(null);
    setSelectedPatient(null);
    setSelectedAnalysis('');
    setSelectedSample('');
    setSelectedFiles({ read1: null, read2: null });
    resetUploadTelemetry();
    setTargetSearch('');
    setTargetSearchResults([]);
    setTargetSearchError('');
    setTargetSearchLoading(false);
    setIsTargetSearchFocused(false);
    setExpandedParentId(null);
    setIsAnalysisLocked(false);
    setIsSampleLocked(false);
  };

  useEffect(() => {
    if (isOpen) {
      const fetchParentProjects = async () => {
        try {
          const response = await fetch('https://service9.szapfs.org/parent-projects');
          if (!response.ok) throw new Error('Network response was not ok');
          const data = await response.json();
          setParentProjects(data.sort((a, b) => a.id - b.id));
        } catch (error) {
          console.error('Error fetching parent projects:', error);
        }
      };
      fetchParentProjects();

      // Handle external patient ID if provided
      if (externalPatientId) {
        const parts = externalPatientId.split('_');
        if (parts.length >= 3) {
          const projectId = parseInt(parts[0]);
          const subprojectId = parts[1];

          setSelectedParentId(projectId);
          setExpandedParentId(projectId);
          setSelectedSubprojectId(subprojectId);
        }
      }
    } else {
      resetSelections();
    }
  }, [isOpen, externalPatientId]);

  useEffect(() => {
    if (isOpen) {
      const fetchAnalysisTypes = async () => {
        try {
          const response = await fetch('https://test-tunnel-b1d9.szapfs.org/supported-analyses');
          if (!response.ok) throw new Error('Network response was not ok');
          const data = await response.json();
          setAnalysisTypes(data.analyses || {});
          setSampleTypes(data.sampleTypes || {});
        } catch (error) {
          console.error('Error fetching analysis types:', error);
        }
      };
      fetchAnalysisTypes();
    }
  }, [isOpen]);

  useEffect(() => {
    const query = targetSearch.trim();
    if (!isOpen || isBulkMode || isUploading || !isTargetSearchFocused || query.length < 2) {
      setTargetSearchResults([]);
      setTargetSearchError('');
      setTargetSearchLoading(false);
      return;
    }

    const controller = new AbortController();
    const debounceTimer = setTimeout(async () => {
      setTargetSearchLoading(true);
      setTargetSearchError('');

      try {
        const response = await fetch(
          `https://service9.szapfs.org/search?term=${encodeURIComponent(query)}`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error('Search failed');
        const data = await response.json();
        const results = (data.patients || [])
          .filter(patient => user?.role === 'Reviewer' ? Number(patient.parentProjectId) === 5 : true)
          .slice(0, 12);

        setTargetSearchResults(results);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error searching upload targets:', error);
          setTargetSearchError('Search failed');
        }
      } finally {
        if (!controller.signal.aborted) {
          setTargetSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      clearTimeout(debounceTimer);
      controller.abort();
    };
  }, [targetSearch, isOpen, isBulkMode, isUploading, isTargetSearchFocused, user?.role]);

  useEffect(() => {
    if (expandedParentId) {
      const fetchSubprojects = async () => {
        try {
          const response = await fetch(
            `https://service9.szapfs.org/parent-project/${expandedParentId}/subprojects`
          );
          if (!response.ok) throw new Error('Network response was not ok');
          const data = await response.json();
          setSubprojects(prev => ({ ...prev, [expandedParentId]: data }));
        } catch (error) {
          console.error('Error fetching subprojects:', error);
        }
      };
      fetchSubprojects();
    }
  }, [expandedParentId]);

  useEffect(() => {
    if (selectedParentId && selectedSubprojectId) {
      const fetchPatients = async () => {
        try {
          const response = await fetch(
            `https://service9.szapfs.org/project/${selectedParentId}/${selectedSubprojectId}/patients`
          );
          if (!response.ok) throw new Error('Network response was not ok');
          const data = await response.json();
          const sortedData = sortPatientsByNumber(data);
          setPatients(sortedData);

          // Auto-select patient if externalPatientId is provided
          if (externalPatientId) {
            const matchingPatient = sortedData.find(p => p.patient_id === externalPatientId);
            if (matchingPatient) {
              setSelectedPatient(matchingPatient);
              console.log('Patient auto-selected:', matchingPatient.patient_id);
            } else {
              console.warn('No matching patient found for ID:', externalPatientId);
              console.warn('Available patients:', sortedData.map(p => p.patient_id));
            }
          } else if (pendingSearchSelectionRef.current) {
            const { patientId, patient } = pendingSearchSelectionRef.current;
            const matchingPatient = sortedData.find(p => p.patient_id === patientId);
            setSelectedPatient(matchingPatient || patient);
            pendingSearchSelectionRef.current = null;
          }
        } catch (error) {
          console.error('Error fetching patients:', error);
        }
      };
      fetchPatients();
    } else {
      setPatients([]);
    }

    // Only reset if no external patient ID
    if (!externalPatientId && !pendingSearchSelectionRef.current) {
      setSelectedPatient(null);
      setSelectedAnalysis('');
      setSelectedSample('');
      setSelectedFiles({ read1: null, read2: null });
    }
  }, [selectedParentId, selectedSubprojectId, externalPatientId]);

  useEffect(() => {
    if (selectedAnalysis && analysisTypes[selectedAnalysis]) {
      setSupportedSamples(analysisTypes[selectedAnalysis].supportedSamples || []);
      // Only reset sample if not locked
      if (!isSampleLocked) {
        setSelectedSample('');
        setSelectedFiles({ read1: null, read2: null });
      }
    } else {
      setSupportedSamples([]);
    }
  }, [selectedAnalysis, analysisTypes, isSampleLocked]);

  // Effect to pre-select analysis and sample types from kitInfo
  useEffect(() => {
    if (kitInfo?.prefilledData && Object.keys(analysisTypes).length > 0 && Object.keys(sampleTypes).length > 0) {
      const { analysisType, sampleType } = kitInfo.prefilledData;

      // Find analysis code by name
      if (analysisType) {
        const analysisEntry = Object.entries(analysisTypes).find(
          ([code, details]) => details.name === analysisType
        );
        if (analysisEntry) {
          setSelectedAnalysis(analysisEntry[0]);
          setIsAnalysisLocked(true);
        }
      }

      // Find sample code by name
      if (sampleType) {
        const sampleEntry = Object.entries(sampleTypes).find(
          ([code, details]) => details.name === sampleType
        );
        if (sampleEntry) {
          setSelectedSample(sampleEntry[0]);
          setIsSampleLocked(true);
        }
      }
    }
  }, [kitInfo, analysisTypes, sampleTypes]);

  useEffect(() => {
    // Effect to notify parent about uploading state
    if (onUploadStatusChange) {
      onUploadStatusChange(isUploading);
    }
  }, [isUploading, onUploadStatusChange]);

  useEffect(() => {
    if (!isOpen || isBulkMode || isUploading) return;

    const scrollTimer = setTimeout(() => {
      const projectTarget = selectedSubprojectRef.current || selectedProjectRef.current;
      projectTarget?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      selectedPatientRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 100);

    return () => clearTimeout(scrollTimer);
  }, [
    isOpen,
    isBulkMode,
    isUploading,
    selectedParentId,
    selectedSubprojectId,
    selectedPatient?.patient_id,
    patients,
    subprojects
  ]);

  const handleParentClick = parentId => {
    pendingSearchSelectionRef.current = null;
    setSelectedParentId(parentId);
    setExpandedParentId(expandedParentId === parentId ? null : parentId);
    if (expandedParentId !== parentId) {
      setSelectedSubprojectId(null);
    }
    // Clear file selections on parent project change
    setSelectedFiles({ read1: null, read2: null });
    setTargetSearch('');
    setTargetSearchResults([]);
    setTargetSearchError('');
    setIsTargetSearchFocused(false);
  };

  const handleSubprojectClick = subprojectId => {
    pendingSearchSelectionRef.current = null;
    setSelectedSubprojectId(subprojectId);
    // Clear file selections on subproject change
    setSelectedFiles({ read1: null, read2: null });
    setTargetSearch('');
    setTargetSearchResults([]);
    setTargetSearchError('');
    setIsTargetSearchFocused(false);
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    setTargetSearch('');
    setTargetSearchResults([]);
    setIsTargetSearchFocused(false);
  };

  const handleTargetSearchSelect = (searchPatient) => {
    const patient = normalizeSearchPatient(searchPatient);
    const parentProjectId = Number(patient.parentProjectId);
    const subprojectId = patient.subprojectId;

    if (!parentProjectId || !subprojectId || !patient.patient_id) {
      console.error('Cannot select upload target from search result:', searchPatient);
      return;
    }

    const parentProject = parentProjects.find(project => Number(project.id) === parentProjectId);
    const existingSubprojects = subprojects[parentProjectId] || parentProject?.subprojects || [];
    const selectedSubproject = {
      project_id: subprojectId,
      parent_project_id: parentProjectId,
      description: patient.subprojectDescription || null
    };
    const hasSubproject = existingSubprojects.some(sub => sub.project_id === subprojectId);

    setSubprojects(prev => ({
      ...prev,
      [parentProjectId]: hasSubproject ? existingSubprojects : [...existingSubprojects, selectedSubproject]
    }));

    const isSameTarget =
      Number(selectedParentId) === parentProjectId &&
      String(selectedSubprojectId) === String(subprojectId);

    pendingSearchSelectionRef.current = isSameTarget
      ? null
      : { patientId: patient.patient_id, patient };

    setSelectedParentId(parentProjectId);
    setExpandedParentId(parentProjectId);
    setSelectedSubprojectId(subprojectId);
    setPatients(prev => (isSameTarget ? prev : [patient]));
    setSelectedPatient(patient);
    setSelectedFiles({ read1: null, read2: null });

    if (!isAnalysisLocked) setSelectedAnalysis('');
    if (!isSampleLocked) setSelectedSample('');

    setTargetSearch('');
    setTargetSearchResults([]);
    setTargetSearchError('');
    setIsTargetSearchFocused(false);
  };

  // Utility function to determine read type from filename - similar to BulkUpload validation logic
  const detectReadType = (fileName) => {
    if (!fileName) return null;
    
    const lowerName = fileName.toLowerCase();
    
    // Check for standard R1/R2 patterns (most common)
    if (lowerName.includes('_r1') || lowerName.includes('-r1') || lowerName.includes('.r1')) return 'read1';
    if (lowerName.includes('_r2') || lowerName.includes('-r2') || lowerName.includes('.r2')) return 'read2';
    
    // Check for patterns with just r1/r2 in name
    if (lowerName.includes('r1')) return 'read1';
    if (lowerName.includes('r2')) return 'read2';
    
    // Also check for _1/_2 pattern (common in FASTQ)
    if (lowerName.includes('_1.') || lowerName.match(/_1$/)) return 'read1';
    if (lowerName.includes('_2.') || lowerName.match(/_2$/)) return 'read2';
    
    return null;
  };

  const handleFileSelect = (event, fileType) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file based on analysis type (FASTQ vs non-FASTQ)
    if (isFastq) {
      // For FASTQ, check the read type
      const detectedType = detectReadType(file.name);
      
      if (!detectedType) {
        alert('FASTQ file names should include R1/R2 or _1/_2 to indicate read type');
        return;
      }
      
      // Auto-correct file placement if needed
      if (detectedType !== fileType) {
        fileType = detectedType;
      }
    }
    
    setSelectedFiles(prev => ({ ...prev, [fileType]: file }));
  };

  // Determine if FASTQ (paired reads) or single file is needed based on analysis type
  const analysisDetails = analysisTypes[selectedAnalysis] || {};
  const isFastq = analysisDetails.uploadType === 'FASTQ';

  const updateR2Progress = (role, progress) => {
    uploadMetricsRef.current.loadedBytes[role] = progress.loadedBytes;
    uploadMetricsRef.current.totalBytes[role] = progress.totalBytes;
    uploadMetricsRef.current.speedBytesPerSecond[role] = progress.speedBytesPerSecond;

    const totalBytes = Object.values(uploadMetricsRef.current.totalBytes)
      .reduce((sum, value) => sum + (value || 0), 0);
    const loadedBytes = Object.values(uploadMetricsRef.current.loadedBytes)
      .reduce((sum, value) => sum + (value || 0), 0);
    const speedBytesPerSecond = Object.values(uploadMetricsRef.current.speedBytesPerSecond)
      .reduce((sum, value) => sum + (value || 0), 0);
    const etaSeconds = speedBytesPerSecond > 0 && totalBytes > loadedBytes
      ? (totalBytes - loadedBytes) / speedBytesPerSecond
      : null;

    setUploadProgress(prev => ({
      ...prev,
      [role]: progress.percent,
      overall: totalBytes > 0 ? (loadedBytes / totalBytes) * 100 : progress.percent
    }));

    setUploadStats(prev => ({
      ...prev,
      [role]: progress,
      overall: {
        loadedBytes,
        totalBytes,
        speedBytesPerSecond,
        etaSeconds
      }
    }));
  };

  const requestR2File = async ({ file, fileRole, uploadId, controller }) => {
    setUploadStage(`Requesting R2 URL for ${file.name}`);
    const payload = {
      ...(uploadId ? { uploadId } : {}),
      patientId: selectedPatient.patient_id,
      subprojectId: selectedSubprojectId,
      analysisCode: selectedAnalysis,
      sampleCode: selectedSample,
      fileRole,
      filename: file.name,
      contentType: DEFAULT_R2_CONTENT_TYPE,
      sizeBytes: file.size,
      kitInfo: normalizeKitInfo(kitInfo)
    };

    const descriptor = await requestR2UploadUrl(payload, controller.signal);
    return {
      ...descriptor,
      uploadUrl: resolveUploadUrl(descriptor),
      fileRole,
      filename: file.name,
      sizeBytes: file.size
    };
  };

  const uploadPreparedR2File = async ({ file, descriptor, progressRole, controller }) => {
    setCurrentFileName(file.name);
    setUploadStage(`Uploading ${file.name} to R2`);

    const result = await putFileToR2({
      uploadUrl: descriptor.uploadUrl,
      file,
      headers: resolveR2Headers(descriptor),
      signal: controller.signal,
      onProgress: progress => updateR2Progress(progressRole, progress)
    });

    return {
      fileRole: descriptor.fileRole,
      filename: file.name,
      r2Key: descriptor.r2Key,
      sizeBytes: file.size,
      ...(result.etag ? { etag: result.etag } : {})
    };
  };

  const handleUpload = async () => {
    // Validate files based on analysis type
    if (isFastq && (!selectedFiles.read1 || !selectedFiles.read2)) {
      alert('FASTQ analysis requires both Read 1 (R1) and Read 2 (R2) files');
      return;
    }
    if (!isFastq && !selectedFiles.read1) {
      alert('Please select a file to upload');
      return;
    }
    
    let filesForUpload = { ...selectedFiles };

    if (isFastq) {
      const read1Type = detectReadType(selectedFiles.read1.name);
      const read2Type = detectReadType(selectedFiles.read2.name);
      
      if (read1Type !== 'read1' || read2Type !== 'read2') {
        if (read1Type === 'read2' && read2Type === 'read1') {
          filesForUpload = {
            read1: selectedFiles.read2,
            read2: selectedFiles.read1
          };
        } else {
          alert('Please ensure your Read 1 file is properly named with R1 and Read 2 file with R2');
          return;
        }
      }
    }
    
    resetUploadTelemetry();
    setIsUploading(true);
    setUploadStage('Preparing R2 upload');
    
    const controller = new AbortController();
    uploadControllerRef.current = controller;

    const metadata = {
      analysisCode: selectedAnalysis,
      sampleCode: selectedSample,
      patientId: selectedPatient.patient_id,
      subprojectId: selectedSubprojectId,
      originalFileName: filesForUpload.read1.name + (isFastq && filesForUpload.read2 ? ',' + filesForUpload.read2.name : '')
    };

    try {
      let logicalUploadId = null;
      let uploadedR2Files = [];

      if (isFastq && selectedFiles.read2) {
        const read1Descriptor = await requestR2File({
          file: filesForUpload.read1,
          fileRole: 'read1',
          controller
        });
        logicalUploadId = read1Descriptor.uploadId;

        const read2Descriptor = await requestR2File({
          file: filesForUpload.read2,
          fileRole: 'read2',
          uploadId: logicalUploadId,
          controller
        });

        uploadedR2Files = await Promise.all([
          uploadPreparedR2File({
            file: filesForUpload.read1,
            descriptor: read1Descriptor,
            progressRole: 'read1',
            controller
          }),
          uploadPreparedR2File({
            file: filesForUpload.read2,
            descriptor: read2Descriptor,
            progressRole: 'read2',
            controller
          })
        ]);
      } else {
        const descriptor = await requestR2File({
          file: filesForUpload.read1,
          fileRole: 'single',
          controller
        });
        logicalUploadId = descriptor.uploadId;
        uploadedR2Files = [await uploadPreparedR2File({
          file: filesForUpload.read1,
          descriptor,
          progressRole: 'read1',
          controller
        })];
      }

      setUploadStage('Confirming R2 upload');

      const confirmPayload = {
        uploadId: logicalUploadId,
        metadata,
        files: uploadedR2Files.map(({ fileRole, filename, r2Key, sizeBytes, etag }) => ({
          fileRole,
          filename,
          r2Key,
          sizeBytes,
          ...(etag ? { etag } : {})
        })),
        ...(kitInfo && {
          kitInfo: normalizeKitInfo(kitInfo)
        })
      };

      await confirmR2Upload(confirmPayload, controller.signal);
      setUploadStage('R2 upload accepted. Backend is downloading the file.');
      setUploadProgress(prev => ({ ...prev, overall: 100 }));

      setTimeout(() => {
        setIsUploading(false);
        setSelectedFiles({ read1: null, read2: null });
        resetSelections();
        onClose();
      }, 2000);
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('Upload was canceled');
      } else {
        console.error('Upload error:', err);
      }
      
      setIsUploading(false);
      resetUploadTelemetry();
    } finally {
      uploadControllerRef.current = null;
    }
  };

  // Cancel upload
  const cancelUpload = () => {
    if (uploadControllerRef.current) {
      uploadControllerRef.current.abort();
      uploadControllerRef.current = null;
      setIsUploading(false);
      resetUploadTelemetry();
    }
  };

  // Cleanup function to cancel upload if component unmounts
  useEffect(() => {
    return () => {
      if (uploadControllerRef.current) {
        uploadControllerRef.current.abort();
        console.log('UploadModal component unmounted, upload aborted');
      }
    };
  }, []);

  const handleBulkUploadStatus = (isUploading) => {
    setIsBulkUploading(isUploading);
  };

  if (!isOpen) return null;

  // Create portal content to render directly to document body
  const showTargetSearch = !isBulkMode && !isUploading;
  const showTargetSearchDropdown =
    showTargetSearch && isTargetSearchFocused && targetSearch.trim().length >= 2;

  const modalContent = (
    <div className="UPM-modal-overlay">
      <div 
        className={`UPM-modal ${isBulkMode ? 'bulk-mode' : ''} ${isUploading ? 'uploading' : ''}`}
      >
        <div className="UPM-modal-header">
          <h2>
            {isBulkMode ? (
              <button 
                className="header-back-button" 
                onClick={() => setIsBulkMode(false)}
                disabled={isBulkUploading}
                style={{ opacity: isBulkUploading ? 0.5 : 1 }}
              >
                <FontAwesomeIcon icon={faArrowLeft} /> Back
              </button>
            ) : (
              'Upload Data'
            )}
          </h2>
          {!isUploading && (
            <button 
              className="UPM-close-button" 
              onClick={onClose}
              disabled={isBulkUploading}
              style={{ opacity: isBulkUploading ? 0.5 : 1 }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
        {showTargetSearch && (
          <div className="UPM-target-search">
            <div className="UPM-target-search-input">
              <FontAwesomeIcon icon={faSearch} />
              <input
                type="text"
                value={targetSearch}
                onChange={event => setTargetSearch(event.target.value)}
                onFocus={() => setIsTargetSearchFocused(true)}
                onBlur={() => setIsTargetSearchFocused(false)}
                placeholder="Search patients, barcodes, projects..."
              />
            </div>
            {showTargetSearchDropdown && (
              <div className="UPM-target-search-dropdown">
                {targetSearchLoading ? (
                  <div className="UPM-target-search-state">Searching...</div>
                ) : targetSearchError ? (
                  <div className="UPM-target-search-state">{targetSearchError}</div>
                ) : targetSearchResults.length > 0 ? (
                  targetSearchResults.map((patient, index) => {
                    const barcode = getPatientBarcode(patient);
                    return (
                      <button
                        type="button"
                        key={patient.patient_id || index}
                        className="UPM-target-search-result"
                        onMouseDown={event => {
                          event.preventDefault();
                          handleTargetSearchSelect(patient);
                        }}
                      >
                        <span className="UPM-target-search-result-main">
                          <span className="UPM-target-search-name">{patient.name || 'No Name'}</span>
                          <span className="UPM-target-search-id">{patient.patient_id}</span>
                        </span>
                        <span className="UPM-target-search-context">
                          {patient.parentProjectName || `Project ${patient.parentProjectId}`} / {patient.subprojectId}
                          {patient.subprojectDescription ? ` - ${patient.subprojectDescription}` : ''}
                        </span>
                        {barcode && (
                          <span className="UPM-target-search-barcode">{barcode}</span>
                        )}
                      </button>
                    );
                  })
                ) : (
                  <div className="UPM-target-search-state">No matching patients</div>
                )}
              </div>
            )}
          </div>
        )}
        <div className="UPM-modal-content">
          {/* Kit Info Banner - shown when kitInfo is provided */}
          {kitInfo && !isBulkMode && !isUploading && (
            <div className="UPM-kit-info-banner">
              <div className="UPM-kit-info-icon">
                <i className="fas fa-kit-medical"></i>
              </div>
              <div className="UPM-kit-info-details">
                <div className="UPM-kit-info-row">
                  <span className="UPM-kit-info-label">Kit ID:</span>
                  <span className="UPM-kit-info-value UPM-kit-id">{kitInfo.activationId}</span>
                </div>
                {kitInfo.patientName && (
                  <div className="UPM-kit-info-row">
                    <span className="UPM-kit-info-label">Patient:</span>
                    <span className="UPM-kit-info-value">{kitInfo.patientName}</span>
                  </div>
                )}
                {kitInfo.patientEmail && (
                  <div className="UPM-kit-info-row">
                    <span className="UPM-kit-info-label">Email:</span>
                    <span className="UPM-kit-info-value">{kitInfo.patientEmail}</span>
                  </div>
                )}
                {kitInfo.prefilledData && (
                  <div className="UPM-kit-info-prefilled">
                    <span className="UPM-kit-info-tag">{kitInfo.prefilledData.analysisType}</span>
                    <span className="UPM-kit-info-tag">{kitInfo.prefilledData.sampleType}</span>
                  </div>
                )}
              </div>
              {kitInfo.kitTypeInfo && (
                <div className="UPM-kit-type-preview">
                  <div className="UPM-kit-type-image">
                    <img
                      src={kitInfo.kitTypeInfo.imageURL}
                      alt={kitInfo.kitTypeInfo.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="UPM-kit-type-fallback" style={{display: 'none'}}>
                      <i className="fas fa-flask"></i>
                    </div>
                  </div>
                  <span className="UPM-kit-type-name">{kitInfo.kitTypeInfo.name}</span>
                </div>
              )}
            </div>
          )}
          {isBulkMode ? (
            <BulkUpload onUploadStatusChange={handleBulkUploadStatus} />
          ) : (
            <>
              {isUploading ? (
                <div className="UPM-upload-progress">
                  <h3>{uploadStage || `Uploading ${currentFileName}`}</h3>
                  <div className="UPM-upload-metrics">
                    <span>{formatBytes(uploadStats.overall.loadedBytes)} / {formatBytes(uploadStats.overall.totalBytes)}</span>
                    <span>{formatBytesPerSecond(uploadStats.overall.speedBytesPerSecond)}</span>
                    <span>ETA {formatDuration(uploadStats.overall.etaSeconds)}</span>
                  </div>
                  <div className="progress-container">
                    <label>Overall: {Math.round(uploadProgress.overall || 0)}%</label>
                    <div className="progress-bar">
                      <div
                        className="progress"
                        style={{
                          width: `${uploadProgress.overall || 0}%`,
                          background: 'linear-gradient(170deg, #2595be7e 0%, #20558aec 100%)'
                        }}
                      />
                    </div>
                  </div>
                  {isFastq ? (
                    <div className='progress-container'>
                      <div className="progress-container">
                        <label>
                          Read 1: {Math.round(uploadProgress.read1 || 0)}%
                          <span className="UPM-file-upload-speed">{formatBytesPerSecond(uploadStats.read1.speedBytesPerSecond)}</span>
                        </label>
                        <div className="progress-bar">
                          <div
                            className="progress"
                            style={{
                              width: `${uploadProgress.read1 || 0}%`,
                              background: 'linear-gradient(170deg, #2595be7e 0%, #20558aec 100%)'
                            }}
                          />
                        </div>
                      </div>
                      <div className="progress-container">
                        <label>
                          Read 2: {Math.round(uploadProgress.read2 || 0)}%
                          <span className="UPM-file-upload-speed">{formatBytesPerSecond(uploadStats.read2.speedBytesPerSecond)}</span>
                        </label>
                        <div className="progress-bar">
                          <div
                            className="progress"
                            style={{
                              width: `${uploadProgress.read2 || 0}%`,
                              background: 'linear-gradient(170deg, #2595be7e 0%, #20558aec 100%)'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="progress-container">
                      <label>
                        File: {Math.round(uploadProgress.read1 || 0)}%
                        <span className="UPM-file-upload-speed">{formatBytesPerSecond(uploadStats.read1.speedBytesPerSecond)}</span>
                      </label>
                      <div className="progress-bar">
                        <div
                          className="progress"
                          style={{
                            width: `${uploadProgress.read1 || 0}%`,
                            background: 'linear-gradient(170deg, #2595be7e 0%, #20558aec 100%)'
                          }}
                        />
                      </div>
                    </div>
                  )}
                  <button className="cancel-upload-button" onClick={cancelUpload}>Cancel Upload</button>
                </div>
              ) : (
                <>
                  <div className="UPM-selection-grid">
                    {!kitInfo && (
                      <>
                        <div className="UPM-form-section">
                          <h3>Project</h3>
                          <div className="UPM-selection-box">
                            <div className="UPM-selection-list">
                              {parentProjects
                                .filter(parent =>
                                  user?.role === 'Reviewer' ? parent.id === 5 : true
                                )
                                .map(parent => (
                                <div key={parent.id}>
                                  <div
                                    ref={selectedParentId === parent.id ? selectedProjectRef : null}
                                    className={`UPM-selection-item ${selectedParentId === parent.id ? 'selected' : ''
                                    }`}
                                    onClick={() => handleParentClick(parent.id)}
                                  >
                                    <span className="UPM-item-name">{parent.name}</span>
                                    <FontAwesomeIcon
                                      icon={expandedParentId === parent.id ? faChevronUp : faChevronDown}
                                      className="UPM-expand-icon"
                                    />
                                  </div>
                                  {expandedParentId === parent.id && subprojects[parent.id] && (
                                    <div className="UPM-subprojects-list">
                                      {subprojects[parent.id].map(sub => (
                                        <div
                                          ref={selectedSubprojectId === sub.project_id ? selectedSubprojectRef : null}
                                          key={sub.project_id}
                                          className={`UPM-subproject-item ${
                                            selectedSubprojectId === sub.project_id ? 'selected' : ''
                                          }`}
                                          onClick={() => handleSubprojectClick(sub.project_id)}
                                        >
                                          <span className="UPM-item-name">Project {sub.project_id}</span>
                                          <span className="UPM-item-id">{sub.description}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="UPM-form-section">
                          <h3>Patient</h3>
                          <div className="UPM-selection-box">
                            <div className="UPM-selection-list">
                              {patients.map(patient => (
                                <div
                                  ref={selectedPatient?.patient_id === patient.patient_id ? selectedPatientRef : null}
                                  key={patient.patient_id}
                                  className={`UPM-selection-item ${
                                    selectedPatient?.patient_id === patient.patient_id ? 'selected' : ''
                                  }`}
                                  onClick={() => handlePatientSelect(patient)}
                                >
                                  <span className="UPM-item-name">{patient.name || 'No Name'}</span>
                                  <span className="UPM-item-id">{patient.patient_id}</span>
                                  {selectedPatient?.patient_id === patient.patient_id && (
                                    <FontAwesomeIcon icon={faCheck} className="UPM-check-icon" />
                                  )}
                                </div>
                              ))}
                              {selectedSubprojectId && patients.length === 0 && (
                                <div className="UPM-no-results">No patients found</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="UPM-form-section">
                      <h3>Analysis Type {isAnalysisLocked && <span className="UPM-locked-badge">From Kit</span>}</h3>
                      <select
                        value={selectedAnalysis}
                        onChange={e => setSelectedAnalysis(e.target.value)}
                        disabled={!selectedPatient || isAnalysisLocked}
                        className={isAnalysisLocked ? 'UPM-locked-select' : ''}
                      >
                        <option value="">Select Analysis Type</option>
                        {Object.entries(analysisTypes).map(([code, details]) => (
                          <option key={code} value={code}>
                            {details.name} ({code})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="UPM-form-section">
                      <h3>Sample Type {isSampleLocked && <span className="UPM-locked-badge">From Kit</span>}</h3>
                      <select
                        value={selectedSample}
                        onChange={e => setSelectedSample(e.target.value)}
                        disabled={(!selectedAnalysis || supportedSamples.length === 0) || isSampleLocked}
                        className={isSampleLocked ? 'UPM-locked-select' : ''}
                      >
                        <option value="">Select Sample Type</option>
                        {supportedSamples.map(code => (
                          <option key={code} value={code}>
                            {sampleTypes[code]?.name} ({code})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="UPM-file-section">
                      <h3>Upload Files</h3>
                      {isFastq ? (
                        <div className="UPM-dual-file-upload">
                          <div className="UPM-file-input">
                            <label className={`UPM-file-input-label ${!selectedSample ? 'disabled' : ''}`}>
                              <input
                                type="file"
                                onChange={e => handleFileSelect(e, 'read1')}
                                accept=".gz"
                                disabled={!selectedSample}
                                style={{ display: 'none' }}
                              />
                              <div className="UPM-file-input-content">
                                <FontAwesomeIcon icon={faCloudArrowUp} className="UPM-file-icon" />
                                <span>Select Read 1 FASTQ</span>
                              </div>
                            </label>
                            {selectedFiles.read1 && (
                              <div className="UPM-selected-file">Selected: {selectedFiles.read1.name}</div>
                            )}
                          </div>
                          <div className="UPM-file-input">
                            <label className={`UPM-file-input-label ${!selectedSample ? 'disabled' : ''}`}>
                              <input
                                type="file"
                                onChange={e => handleFileSelect(e, 'read2')}
                                accept=".gz"
                                disabled={!selectedSample}
                                style={{ display: 'none' }}
                              />
                              <div className="UPM-file-input-content">
                                <FontAwesomeIcon icon={faCloudArrowUp} className="UPM-file-icon" />
                                <span>Select Read 2 FASTQ</span>
                              </div>
                            </label>
                            {selectedFiles.read2 && (
                              <div className="UPM-selected-file">Selected: {selectedFiles.read2.name}</div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="UPM-file-input">
                          <label className={`UPM-file-input-label ${!selectedSample ? 'disabled' : ''}`}>
                            <input
                              type="file"
                              onChange={e => handleFileSelect(e, 'read1')}
                              disabled={!selectedSample}
                              style={{ display: 'none' }}
                            />
                            <div className="UPM-file-input-content">
                              <FontAwesomeIcon icon={faCloudArrowUp} className="UPM-file-icon" />
                              <span>Select Single File</span>
                            </div>
                          </label>
                          {selectedFiles.read1 && (
                            <div className="UPM-selected-file">Selected: {selectedFiles.read1.name}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="UPM-modal-footer">
                    {user?.role !== 'Reviewer' && (
                      <button className="bulk-upload-btn" onClick={() => setIsBulkMode(true)}>
                        Bulk Upload
                      </button>
                    )}
                    {!isUploading && (
                      <button
                        className="UPM-upload-button"
                        onClick={handleUpload}
                        disabled={
                          !selectedParentId ||
                          !selectedSubprojectId ||
                          !selectedPatient ||
                          !selectedAnalysis ||
                          !selectedSample ||
                          !selectedFiles.read1 ||
                          (isFastq && !selectedFiles.read2)
                        }
                      >
                        <FontAwesomeIcon icon={faCloudArrowUp} /> Upload
                      </button>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
  
  // Use createPortal to render modal directly to document body
  return createPortal(modalContent, document.body);
}

export default UploadModal;
